import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import type {
  Tables,
  TablesInsert,
  TablesUpdate,
} from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  toLegacyTimestamp,
  usePendingMutation,
  type MutationOptions,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

export type Studio = Tables<"studios">;
export type StudioInsert = TablesInsert<"studios">;
export type StudioUpdate = TablesUpdate<"studios">;

interface UseStudiosOptions {
  search?: string;
}

type ZeroStudio = {
  id: string;
  number?: string | null;
  name: string;
  description?: string | null;
  bridgeURL?: string | null;
  bridgeAPIKey?: string | null;
  location?: string | null;
  unityHostname?: string | null;
  activeConfigID?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  deletedAt?: number | string | null;
  version?: number | null;
};

function toLegacyStudio(row: ZeroStudio): Studio {
  return {
    id: row.id,
    number: row.number ?? null,
    name: row.name,
    description: row.description ?? null,
    bridge_url: row.bridgeURL ?? null,
    bridge_api_key: row.bridgeAPIKey ?? null,
    location: row.location ?? null,
    unity_hostname: row.unityHostname ?? null,
    active_config_id: row.activeConfigID ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    deleted_at: row.deletedAt ? toLegacyTimestamp(row.deletedAt) : null,
    version: row.version ?? null,
  };
}

function toZeroStudioInput(studio: StudioInsert, id = generateUUID()) {
  return {
    id,
    name: studio.name,
    number: studio.number ?? null,
    description: studio.description ?? null,
    bridgeURL: studio.bridge_url ?? null,
    bridgeAPIKey: studio.bridge_api_key ?? null,
    location: studio.location ?? null,
    unityHostname: studio.unity_hostname ?? null,
    activeConfigID: studio.active_config_id ?? null,
  };
}

function toZeroStudioUpdate(id: string, studio: StudioUpdate) {
  return {
    id,
    ...(studio.name !== undefined ? { name: studio.name } : {}),
    ...(studio.number !== undefined ? { number: studio.number } : {}),
    ...(studio.description !== undefined
      ? { description: studio.description }
      : {}),
    ...(studio.bridge_url !== undefined
      ? { bridgeURL: studio.bridge_url }
      : {}),
    ...(studio.bridge_api_key !== undefined
      ? { bridgeAPIKey: studio.bridge_api_key }
      : {}),
    ...(studio.location !== undefined ? { location: studio.location } : {}),
    ...(studio.unity_hostname !== undefined
      ? { unityHostname: studio.unity_hostname }
      : {}),
    ...(studio.active_config_id !== undefined
      ? { activeConfigID: studio.active_config_id }
      : {}),
  };
}

export const useStudios = (options: UseStudiosOptions = {}) => {
  const { search } = options;
  const [data, details] = useQuery(appQueries.studios.list());
  const searchTerm = search?.trim().toLowerCase();
  const studios = ((data ?? []) as ZeroStudio[])
    .map(toLegacyStudio)
    .filter((studio) => {
      if (!searchTerm) return true;
      return [studio.name, studio.location, studio.number].some((value) =>
        (value ?? "").toLowerCase().includes(searchTerm),
      );
    });

  return {
    data: studios,
    isLoading: details.type !== "complete" && studios.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useSingleStudio = (id: string | undefined) => {
  const query = id ? appQueries.studios.byID({ id }) : undefined;
  const [data, details] = useQuery(query, { enabled: !!id });

  return {
    data: data ? toLegacyStudio(data as ZeroStudio) : null,
    isLoading: !!id && details.type !== "complete" && !data,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useCreateStudio = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (studio: StudioInsert) =>
    runPending(async () => {
      const input = toZeroStudioInput(studio, studio.id ?? generateUUID());
      const mutation = zero.mutate(appMutators.studios.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio create was rejected",
      );
      return toLegacyStudio(input);
    });

  return {
    mutate: (studio: StudioInsert, options?: MutationOptions<Studio>) => {
      void run(studio)
        .then((data) => {
          toast.success("Studio created");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to create studio: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
};

export const useUpdateStudio = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = ({ id, studio }: { id: string; studio: StudioUpdate }) =>
    runPending(async () => {
      const input = toZeroStudioUpdate(id, studio);
      const mutation = zero.mutate(appMutators.studios.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio update was rejected",
      );
      return toLegacyStudio({ ...input, name: input.name ?? "" });
    });

  return {
    mutate: (
      input: { id: string; studio: StudioUpdate },
      options?: MutationOptions<Studio>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Studio updated");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to update studio: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
};

export const useSoftDeleteStudio = () => {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (id: string) =>
    runPending(async () => {
      const mutation = zero.mutate(appMutators.studios.softDelete({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Studio delete was rejected",
      );
    });

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Studio deleted");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to delete studio: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
};
